import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Stethoscope, 
  ShieldCheck, 
  Heart, 
  FileSearch,
  Users,
  CheckCircle2,
  ArrowRight,
  Upload,
  Brain,
  UserCheck,
  Building2,
  Calendar,
  Award,
  Star,
  Sparkles,
  Info
} from 'lucide-react';

const MedicallyLedAdvantage = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 5);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const advantageCards = [
    {
      id: 1,
      icon: Stethoscope,
      title: 'Clinical Understanding',
      description: 'Founded by medical professionals who understand clinical roles, competencies, and what matters in healthcare.',
      color: 'from-teal-400 to-cyan-500',
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600',
      details: [
        'Understanding of clinical workflows and requirements',
        'Knowledge of specialty-specific competencies',
        'Patient safety-focused approach',
        'Recognition of skills that matter in healthcare settings'
      ]
    },
    {
      id: 2,
      icon: FileSearch,
      title: 'Structured Review Process',
      description: 'Organised document collection and review workflows to support credential checks and compliance.',
      color: 'from-blue-400 to-indigo-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      details: [
        'Structured document collection workflows',
        'Organised credential review process',
        'Support for compliance documentation',
        'Clear tracking of submission status'
      ]
    },
    {
      id: 3,
      icon: Heart,
      title: 'Transparent & Ethical',
      description: 'Clear, honest communication throughout. No inflated claims, no hidden processes.',
      color: 'from-pink-400 to-rose-500',
      bgColor: 'bg-pink-50',
      iconColor: 'text-pink-600',
      details: [
        'Transparent recruitment process',
        'Honest communication at every step',
        'Clear expectations for candidates and employers',
        'Ethical approach to healthcare recruitment'
      ]
    }
  ];

  const workflowSteps = [
    {
      id: 1,
      icon: Upload,
      title: 'Document Submission',
      description: 'Candidates upload CV and credentials',
      color: 'bg-purple-500'
    },
    {
      id: 2,
      icon: Brain,
      title: 'AI-Assisted Organisation',
      description: 'Documents organised and parsed',
      color: 'bg-blue-500'
    },
    {
      id: 3,
      icon: UserCheck,
      title: 'Profile Review',
      description: 'Review for completeness',
      color: 'bg-teal-500'
    },
    {
      id: 4,
      icon: Building2,
      title: 'Employer Access',
      description: 'Employers browse candidates',
      color: 'bg-green-500'
    },
    {
      id: 5,
      icon: Calendar,
      title: 'Interview Options',
      description: 'Review video or schedule live',
      color: 'bg-indigo-500'
    }
  ];

  const badges = [
    {
      icon: ShieldCheck,
      title: 'Profile Reviewed',
      subtitle: 'Documents checked for completeness',
      description: 'Profile and documents have been reviewed for completeness and relevance. This is not a regulatory or licensing approval.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200'
    },
    {
      icon: Award,
      title: 'Clinician Reviewed',
      subtitle: 'Background assessed for suitability',
      description: 'Background and experience have been reviewed for role suitability and professional alignment. This is not an employment guarantee.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      icon: Star,
      title: 'Video Submitted',
      subtitle: 'Interview video available',
      description: 'Candidate has completed a structured video interview which is available for employer review.',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-cyan-50/30 to-white">
      <Navbar />
      
      <div className="pt-24 pb-20">
        {/* SECTION 1: Intro Banner */}
        <section className={`px-4 sm:px-6 lg:px-8 py-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-200 px-6 py-3 rounded-full mb-8">
              <Sparkles className="h-5 w-5 text-teal-600" />
              <span className="text-sm font-semibold text-teal-700">Healthcare-Led Platform</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Medical Recruitment by{' '}
              <span className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent">
                a Medical Professional
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-12 leading-relaxed">
              Founded by medical professionals with over 15 years of clinical and healthcare leadership experience — 
              bringing genuine understanding to healthcare recruitment.
            </p>
            
            <Button 
              size="lg" 
              onClick={() => setShowModal(true)}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group"
            >
              Learn More
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </section>

        {/* SECTION 2: Why It Matters - Interactive Cards */}
        <section className="px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Our Approach
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                What healthcare expertise brings to recruitment
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {advantageCards.map((card, index) => {
                const Icon = card.icon;
                const isActive = activeCard === card.id;
                
                return (
                  <Card 
                    key={card.id}
                    className={`cursor-pointer transition-all duration-500 hover:shadow-2xl border-2 ${
                      isActive ? 'scale-105 shadow-2xl border-teal-400' : 'border-gray-200 hover:border-teal-300'
                    }`}
                    onClick={() => setActiveCard(isActive ? null : card.id)}
                    style={{
                      transitionDelay: `${index * 100}ms`
                    }}
                  >
                    <CardContent className="p-8">
                      <div className={`w-16 h-16 rounded-2xl ${card.bgColor} flex items-center justify-center mb-6 transition-transform duration-500 ${
                        isActive ? 'scale-110 rotate-6' : ''
                      }`}>
                        <Icon className={`h-8 w-8 ${card.iconColor}`} />
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {card.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {card.description}
                      </p>
                      
                      <div className={`overflow-hidden transition-all duration-500 ${
                        isActive ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <div className="border-t border-gray-200 pt-6 mt-6">
                          <h4 className="font-semibold text-gray-900 mb-3">What This Means:</h4>
                          <ul className="space-y-2">
                            {card.details.map((detail, idx) => (
                              <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700">
                                <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <span className={`text-sm font-medium ${card.iconColor} flex items-center group`}>
                          {isActive ? 'Click to collapse' : 'Click to learn more'}
                          <ArrowRight className={`ml-2 h-4 w-4 transition-transform ${
                            isActive ? 'rotate-90' : 'group-hover:translate-x-1'
                          }`} />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 3: Workflow Animation */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A structured recruitment workflow
              </p>
            </div>
            
            <div className="relative">
              <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-purple-300 via-teal-300 to-indigo-300 mx-auto" 
                   style={{width: '80%', marginLeft: '10%'}}>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative z-10">
                {workflowSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = activeStep === index;
                  
                  return (
                    <div 
                      key={step.id}
                      className={`text-center transition-all duration-700 ${
                        isActive ? 'scale-110' : 'scale-100 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <div className={`relative mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-4 transition-all duration-500 ${
                        isActive 
                          ? `${step.color} shadow-2xl ring-4 ring-white ring-offset-4 ring-offset-transparent` 
                          : 'bg-white border-4 border-gray-200'
                      }`}>
                        <Icon className={`h-10 w-10 ${
                          isActive ? 'text-white' : 'text-gray-400'
                        } transition-colors duration-500`} />
                        
                        {isActive && (
                          <div className="absolute inset-0 rounded-full bg-white/30 animate-ping"></div>
                        )}
                        
                        <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          isActive ? 'bg-white text-gray-900' : 'bg-gray-200 text-gray-600'
                        } transition-colors duration-500`}>
                          {step.id}
                        </div>
                      </div>
                      
                      <h3 className={`font-bold mb-2 transition-all duration-500 ${
                        isActive ? 'text-gray-900 text-lg' : 'text-gray-700 text-base'
                      }`}>
                        {step.title}
                      </h3>
                      
                      <p className={`text-sm transition-colors duration-500 ${
                        isActive ? 'text-gray-700' : 'text-gray-500'
                      }`}>
                        {step.description}
                      </p>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-12 flex justify-center space-x-3">
                {workflowSteps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveStep(index)}
                    className={`h-2 rounded-full transition-all duration-500 ${
                      activeStep === index 
                        ? 'w-12 bg-gradient-to-r from-teal-600 to-cyan-600' 
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: Badge System with Clear Definitions */}
        <section className="px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Status Indicators
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Clear labels to show candidate progress — with transparent definitions
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {badges.map((badge, index) => {
                const Icon = badge.icon;
                
                return (
                  <div 
                    key={index}
                    className="group"
                    style={{transitionDelay: `${index * 100}ms`}}
                  >
                    <Card className={`border-2 ${badge.borderColor} ${badge.bgColor} hover:shadow-xl transition-shadow h-full`}>
                      <CardContent className="p-8 text-center">
                        <div className="relative inline-block mb-6">
                          <div className={`w-20 h-20 rounded-full ${badge.bgColor} border-4 ${badge.borderColor} flex items-center justify-center`}>
                            <Icon className={`h-10 w-10 ${badge.color}`} />
                          </div>
                        </div>
                        
                        <h3 className={`text-xl font-bold mb-2 ${badge.color}`}>
                          {badge.title}
                        </h3>
                        
                        <p className="text-sm text-gray-600 mb-4 font-medium">
                          {badge.subtitle}
                        </p>
                        
                        {/* Clear Definition Box */}
                        <div className="bg-white/70 border border-gray-200 rounded-lg p-4 text-left">
                          <div className="flex items-start space-x-2">
                            <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-gray-600 leading-relaxed">
                              {badge.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
            
            {/* Important Disclaimer */}
            <div className="mt-12 p-6 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Info className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-amber-800 mb-2">Important Note</h4>
                  <p className="text-sm text-amber-700">
                    Status indicators reflect internal review processes and do not constitute regulatory approval, 
                    licensing confirmation, or employment guarantees. All hiring decisions remain with employers, 
                    and candidates are responsible for ensuring they meet regulatory requirements for their intended role and location.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto">
            <Card className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 border-0 shadow-2xl">
              <CardContent className="p-12 text-center text-white">
                <Users className="h-16 w-16 mx-auto mb-6 opacity-90" />
                <h2 className="text-4xl font-bold mb-4">
                  Join Our Growing Network
                </h2>
                <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
                  Be part of an early-stage platform built with healthcare expertise and a commitment to transparency.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-lg"
                    onClick={() => window.location.href = '/join-talent-pool'}
                  >
                    Upload Your CV
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                    onClick={() => window.location.href = '/register/employer'}
                  >
                    Join as Employer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-600 to-cyan-600 flex items-center justify-center">
                <Stethoscope className="h-6 w-6 text-white" />
              </div>
              <DialogTitle className="text-2xl">Why Healthcare Expertise Matters</DialogTitle>
            </div>
            <DialogDescription className="text-base leading-relaxed space-y-4 pt-4">
              <p>
                <strong className="text-teal-700">Luma Med Global</strong> was founded by medical professionals 
                with over 15 years of experience across clinical practice and healthcare leadership.
              </p>
              
              <div className="bg-teal-50 border-l-4 border-teal-600 p-4 rounded">
                <p className="font-semibold text-teal-900 mb-2">What This Means:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Deep understanding of healthcare systems and workforce needs</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Knowledge of clinical competencies and role requirements</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Commitment to ethical and transparent recruitment practices</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Focus on quality and patient safety considerations</span>
                  </li>
                </ul>
              </div>
              
              <p>
                As an early-stage platform, we&apos;re building a network of healthcare professionals and employers 
                with honesty and integrity at our core.
              </p>
              
              <div className="pt-4">
                <Button 
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                  onClick={() => setShowModal(false)}
                >
                  Get Started
                </Button>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default MedicallyLedAdvantage;
